import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import caretRight from "../assets/images/Home/CaretRight.svg";
// import heroThumbnail from "../../public/hero_thumbnail.webp";
import heroVideo from "../assets/images/Home/vancouver_bg_video.mp4";
import EarlyClosures from "./EarlyClosures";

const sanitize = (v) => (typeof v === "string" ? v.trim() : "");

const isInternalUrl = (url) => {
    if (!url) return false;
    if (url.startsWith("/")) return true;
    if (url.startsWith("http") || url.startsWith("mailto") || url.startsWith("tel"))
        return false;
    return true;
};

// Helper function to clean up URLs with duplicate query parameters
const cleanUrl = (url) => {
    if (!url || typeof url !== "string") return "";
    
    try {
        const urlObj = new URL(url);
        const params = new URLSearchParams();
        
        // Collect unique parameters (last occurrence wins for duplicates)
        const seenParams = new Set();
        const paramPairs = urlObj.search.substring(1).split("&");
        
        // Process in reverse to keep last occurrence
        for (let i = paramPairs.length - 1; i >= 0; i--) {
            const pair = paramPairs[i];
            if (!pair) continue;
            
            const [key] = pair.split("=");
            if (key && !seenParams.has(key)) {
                seenParams.add(key);
                params.append(key, pair.split("=")[1] || "");
            }
        }
        
        // Rebuild URL without duplicates
        urlObj.search = params.toString();
        return urlObj.toString();
    } catch (e) {
        // If URL parsing fails, try simple string replacement for common duplicate patterns
        return url.replace(/([?&]ver=\d+[^&]*&ver=\d+)/g, (match) => {
            const versions = match.match(/ver=(\d+)/g);
            return versions ? `?ver=${versions[versions.length - 1].split("=")[1]}` : match;
        });
    }
};

/**
 * Strip h1–h6 tags from API heading HTML so the hero renders a single <h1>.
 * Preserves inner content (spans, strong, etc.) for existing CSS (.hero-content h1 span).
 */
const stripHeadingTags = (html) => {
    if (!html || typeof html !== "string") return "";
    let out = html
        .replace(/<\/h[1-6]>\s*<h[1-6][^>]*>/gi, " ")
        .replace(/<h[1-6][^>]*>/gi, "")
        .replace(/<\/h[1-6]>/gi, "");
    return out.trim();
};

const HeroSection = ({ fields }) => {
    /* ---------------- API DATA ---------------- */
    const backgroundType = fields?.background_type; // "video" | "image"

    const videoUrl = heroVideo; // Use static video file
    const imageUrl = fields?.background_image?.url || "";
    const mobileImage = fields?.background_image_mobile?.url || "";
    // const posterImage ="/public/hero_thumbnail.webp";
    const posterImage = "/hero_thumbnail.webp";


    /* ---------------- VIDEO LOADING OPTIMIZATION ---------------- */
    const videoRef = useRef(null);
    const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
    const [videoLoaded, setVideoLoaded] = useState(false);
    const [showVideo, setShowVideo] = useState(false);

    useEffect(() => {
        const onLoad = () => setShowVideo(true);
        window.addEventListener("load", onLoad);
        return () => window.removeEventListener("load", onLoad);
    }, []);

    // Preload poster image with high priority for faster LCP
    // useEffect(() => {
    //     if (!posterImage) return;

    //     const link = document.createElement('link');
    //     link.rel = 'preload';
    //     link.as = 'image';
    //     link.href = posterImage;
    //     link.fetchPriority = 'high';
    //     document.head.appendChild(link);

    //     return () => {
    //         if (link.parentNode) {
    //             link.parentNode.removeChild(link);
    //         }
    //     };
    // }, [posterImage]);

    // Simplified video loading - minimal delay for smooth transition
    useEffect(() => {
        if (backgroundType !== "video" || !videoUrl) return;

        let loadTimeout = null;

        const startVideoLoad = () => {
            // Short delay for smooth poster-to-video transition
            loadTimeout = setTimeout(() => {
                setShouldLoadVideo(true);
            }, 300); // Reduced from 2000ms to 300ms for faster perceived load
        };

        // Start loading immediately if page is ready, otherwise wait for load
        if (document.readyState === "complete") {
            startVideoLoad();
        } else {
            window.addEventListener("load", startVideoLoad, { once: true });
        }

        return () => {
            if (loadTimeout) clearTimeout(loadTimeout);
        };
    }, [backgroundType, videoUrl]);

    // Handle video load event
    useEffect(() => {
        const video = videoRef.current;
        if (!video || !shouldLoadVideo) return;

        const handleLoadedData = () => {
            setVideoLoaded(true);
        };

        video.addEventListener("loadeddata", handleLoadedData, { once: true });

        return () => {
            video.removeEventListener("loadeddata", handleLoadedData);
        };
    }, [shouldLoadVideo]);

    /* ---------------- SAFE GUARD ---------------- */
    if (!fields) return null;

    /* ---------------- LIST ITEMS ---------------- */
    const listItems = Array.isArray(fields?.list_items)
        ? fields.list_items.filter(
            (i) => sanitize(i?.title) || sanitize(i?.description) || i?.icon?.url
        )
        : [];

    /* ---------------- BACKGROUND STYLE ---------------- */
    const sectionStyle =
        backgroundType === "image" && imageUrl
            ? { backgroundImage: `url(${imageUrl})` }
            : {};

    const safeHeadingHtml = stripHeadingTags(fields?.heading ?? "");

    /* ---------------- JSX ---------------- */
    return (
        <div className="hero-page-cont">
            <section className="hero">
                <div className="hero-inner" style={sectionStyle}>

                    {/* VIDEO ONLY IF API SAYS VIDEO - OPTIMIZED FOR PAGE SPEED */}
                    {backgroundType === "video" && videoUrl && (
                        <>
                            {/* Show poster image while video loads or as fallback */}
                            {posterImage && (
                                <img
                                    src={posterImage}
                                    alt={fields?.heading ? `${sanitize(fields.heading).replace(/<[^>]*>/g, '')} - Video Poster` : "Vancouver Lookout"}
                                    className="hero-bg-video-poster"
                                    fetchPriority="high"
                                    loading="eager"
                                    style={{
                                        position: "absolute",
                                        inset: 0,
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        objectPosition: "center",
                                        zIndex: videoLoaded ? -1 : 1,
                                        opacity: videoLoaded ? 0 : 1,
                                        pointerEvents: "none",
                                    }}
                                    aria-hidden="true"
                                />
                            )}
                            {/* Video element - only loads when shouldLoadVideo is true */}
                            <video
                                ref={videoRef}
                                className="hero-bg-video"
                                autoPlay
                                muted
                                loop
                                playsInline
                                preload="metadata"                               
                                style={{
                                    width: videoLoaded ? "100%" : "1px",
                                    height: videoLoaded ? "100%" : "1px",
                                    opacity: videoLoaded ? 1 : 0,
                                    transition: "opacity 0.5s ease",
                                    position: "absolute",
                                    inset: 0,
                                }}
                                onLoadedData={() => setVideoLoaded(true)}
                            >
                                {shouldLoadVideo && (
                                    <source src={videoUrl} type="video/mp4" />
                                )}
                            </video>
                        </>
                    )}

                    {/* MOBILE IMAGE ONLY IF API SAYS IMAGE */}
                    {backgroundType === "image" && mobileImage && (
                        <style>
                            {`
                @media (max-width: 360px) {
                  .hero-inner {
                    background-image: url(${mobileImage}) !important;
                  }
                }
              `}
                        </style>
                    )}

                    <div className="hero-inner-inner">
                        {/* <div className="hero-content">
                            {fields?.heading && (
                                <h1 dangerouslySetInnerHTML={{ __html: fields.heading }} />
                            )}
                        </div> */}
                        <div className="hero-content">
                            <h1
                                dangerouslySetInnerHTML={{
                                    __html: safeHeadingHtml || "&nbsp;",
                                }}
                            />
                        </div>

                        {listItems.length > 0 && (
                            <div className="hero-bottom-card">
                                <ul className="hc-list hc-horizontal">
                                    {listItems.map((item, index) => {
                                        let link = sanitize(item?.url) || "#";

                                        if (link && link !== "#") {
                                            if (link.startsWith("//")) link = link.substring(1);
                                            link = link.replace(/^\/+/, "/");
                                            if (
                                                !link.startsWith("/") &&
                                                !link.startsWith("http") &&
                                                !link.startsWith("mailto:") &&
                                                !link.startsWith("tel:")
                                            ) {
                                                link = `/${link}`;
                                            }
                                        }

                                        const isBuyNowPopup = link === "#buynow-popup";
                                        const isInternal = isInternalUrl(link);

                                        const Content = (
                                            <li>
                                                <div className="icon">
                                                    {item.icon?.url && <img src={item.icon.url} alt={item.icon?.alt || item.title || "Icon"} />}
                                                </div>
                                                <div className="info">
                                                    <strong>{item.title}</strong>
                                                    <span>{item.description}</span>
                                                </div>
                                                <div className="side-arrow">
                                                    <img src={caretRight} alt="Arrow icon" />
                                                </div>
                                            </li>
                                        );

                                        if (isBuyNowPopup) {
                                            return (
                                                <div
                                                    key={index}
                                                    className="hc-link-wrapper"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();

                                                        // Trigger Xola checkout
                                                        if (typeof window.openXolaCheckout === 'function') {
                                                            window.openXolaCheckout();
                                                        }
                                                    }}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    {Content}
                                                </div>
                                            );
                                        }

                                        if (isInternal) {
                                            return (
                                                <Link key={index} to={link} className="hc-link-wrapper">
                                                    {Content}
                                                </Link>
                                            );
                                        }

                                        return (
                                            <a
                                                key={index}
                                                href={link}
                                                className="hc-link-wrapper"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {Content}
                                            </a>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Reuse shared EarlyClosures component for alerts */}
            <EarlyClosures fields={fields} parentClass="canvas alerts homepage-alerts" />
        </div>
    );
};

export default HeroSection;